import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Navigation, MapPin, ChevronDown } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, MAPS_URL, ADDRESS } from "@/constants/site";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src="/images/hero.jpg"
          alt="Автосервиз АутоДе 1 - работилница"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/60" />
      <div className="hero-grid-lines absolute inset-0" />
      <div className="absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-[#39FF14]/10 blur-[140px]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-[#39FF14] sm:text-sm"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-[#39FF14]" />
          Професионален автосервиз — Хасково
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tighter">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block text-5xl sm:text-7xl lg:text-8xl">
              АутоДе 1
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="text-stroke-green block text-5xl sm:text-7xl lg:text-8xl">
              ЕООД
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-8 max-w-xl text-base text-zinc-400 sm:text-lg"
          data-testid="hero-subtitle"
        >
          Диагностика, механика, автоклиматици, акумулатори и още. Работим предимно с
          немски марки леки автомобили и джипове — с гарантирани произход и качество.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            data-testid="hero-call-button"
            href={PHONE_TEL}
            className="green-glow group flex items-center justify-center gap-3 bg-[#39FF14] px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-black transition-colors duration-300 hover:bg-[#32E612]"
          >
            <Phone size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:rotate-12" />
            Обади се: {PHONE_DISPLAY}
          </a>
          <a
            data-testid="hero-navigate-button"
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-center gap-3 border border-[#39FF14]/40 px-8 py-4 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors duration-300 hover:border-[#39FF14] hover:bg-[#39FF14]/10"
          >
            <Navigation size={18} strokeWidth={2.5} className="text-[#39FF14] transition-transform duration-300 group-hover:translate-x-1" />
            Навигация
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex items-center gap-2 text-sm text-zinc-500"
          data-testid="hero-address"
        >
          <MapPin size={16} className="shrink-0 text-[#39FF14]" />
          {ADDRESS}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden text-[#39FF14] md:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={28} />
        </motion.div>
      </motion.div>
    </section>
  );
}
